import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useState } from 'react';
import { Platform, Pressable, TextInput, View } from 'react-native';

interface SearchBarProps {
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  onClear?: () => void;
  onSubmit?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  height?: number;
  containerClassName?: string;
}

export const SearchBar = React.forwardRef<
  React.ElementRef<typeof TextInput>,
  SearchBarProps
>(
  (
    {
      value,
      onChangeText,
      placeholder = 'Search...',
      onClear,
      onSubmit,
      onFocus,
      onBlur,
      height = 48,
      containerClassName,
    },
    ref
  ) => {
    const theme = useAppTheme();
    const [isFocused, setIsFocused] = useState(false);

    const handleClear = () => {
      onChangeText('');
      onClear?.();
    };

    const handleFocus = () => {
      setIsFocused(true);
      onFocus?.();
    };

    const handleBlur = () => {
      setIsFocused(false);
      onBlur?.();
    };

    return (
      <View
        className={containerClassName ?? 'flex-row items-center rounded-lg px-4 mb-4 bg-surface'}
        style={{
          height,
          borderWidth: isFocused ? 1 : 0,
          borderColor: isFocused ? theme.colors.primary + '40' : 'transparent',
        }}
      >
        {isFocused && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: theme.colors.primary + '12',
              borderRadius: 8,
              pointerEvents: 'none',
            }}
          />
        )}
        <Ionicons
          name="search"
          size={20}
          className="text-muted-foreground"
          style={{ marginRight: 8 }}
        />
        <TextInput
          ref={ref}
          placeholder={placeholder}
          placeholderTextColor={theme.colors.textMuted}
          value={value}
          onChangeText={onChangeText}
          onSubmitEditing={onSubmit}
          onFocus={handleFocus}
          onBlur={handleBlur}
          returnKeyType="search"
          className="flex-1"
          style={{
            color: theme.colors.text,
            fontSize: 16,
            padding: 0,
            margin: 0,
            flex: 1,
            height: '100%',
            borderWidth: 0,
            ...(Platform.OS === 'web' && {
              outline: 'none',
              border: 'none',
              boxShadow: 'none',
              WebkitAppearance: 'none',
            }),
          }}
        />
        {value.length > 0 && (
          <Pressable
            onPress={handleClear}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            accessibilityRole="button"
            accessibilityLabel="Clear search"
          >
            <Ionicons
              name="close-circle"
              size={20}
              className="text-muted-foreground"
            />
          </Pressable>
        )}
      </View>
    );
  }
);

SearchBar.displayName = 'SearchBar';

export default SearchBar;
